// src/components/MobileFileSelector/index.tsx
import { FC, useState, useRef, ChangeEvent, ReactNode } from 'react';
import { Drawer } from 'antd';
import { CloseOutlined, PlusOutlined, CheckOutlined } from '@ant-design/icons';
import SvgIcon from '@/components/SvgIcon';
import styles from './index.module.scss';

interface FileOption {
  value: string;
  label: string;
}

interface MobileFileSelectorProps {
  label: string;
  required?: boolean;
  placeholder?: string;
  options: FileOption[];
  value?: string;
  onChange?: (value: string) => void;
  onFileSelect?: (file: File) => void;
  drawerTitle?: string;
  uploadText?: string;
  prefixIcon?: ReactNode;
}

const MobileFileSelector: FC<MobileFileSelectorProps> = ({
  label,
  required = false,
  placeholder = 'Select...',
  options,
  value,
  onChange,
  onFileSelect,
  drawerTitle = 'Select a CV',
  uploadText = 'Upload new CV',
  prefixIcon,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption?.label || '';

  const handleTriggerClick = () => {
    setDrawerOpen(true);
  };

  const handleOptionClick = (optionValue: string) => {
    onChange?.(optionValue);
    setDrawerOpen(false);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect?.(file);
      setDrawerOpen(false);
    }
    e.target.value = '';
  };

  // Filter out upload option from display list
  const displayOptions = options.filter((opt) => opt.value !== '__upload_new__');

  return (
    <div className={styles.mobileFileSelector}>
      {/* Label */}
      <div className={styles.label}>
        <span className={styles.labelText}>{label}</span>
        {required && <span className={styles.required}>*</span>}
      </div>

      {/* Trigger Button */}
      <button type="button" className={styles.trigger} onClick={handleTriggerClick}>
        <div className={styles.triggerContent}>
          {prefixIcon && value && <span className={styles.prefixIcon}>{prefixIcon}</span>}
          <span className={displayValue ? styles.triggerValue : styles.triggerPlaceholder}>{displayValue || placeholder}</span>
        </div>
        <SvgIcon name="caret-down" size={20} className={styles.caretIcon} />
      </button>

      {/* Bottom Sheet Drawer */}
      <Drawer
        placement="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        height="auto"
        className={styles.drawer}
        styles={{
          header: { display: 'none' },
          body: { padding: 0 },
        }}
      >
        <div className={styles.drawerContent}>
          {/* Header */}
          <div className={styles.drawerHeader}>
            <h3 className={styles.drawerTitle}>{drawerTitle}</h3>
            <button type="button" className={styles.closeBtn} onClick={() => setDrawerOpen(false)} aria-label="Close">
              <CloseOutlined />
            </button>
          </div>

          {/* Options List */}
          <div className={styles.optionsList}>
            {displayOptions.map((option) => {
              const isSelected = option.value === value;
              return (
                <button key={option.value} type="button" className={`${styles.optionItem} ${isSelected ? styles.optionItemSelected : ''}`} onClick={() => handleOptionClick(option.value)}>
                  <span className={styles.optionLabel}>{option.label}</span>
                  {isSelected && <CheckOutlined className={styles.checkIcon} />}
                </button>
              );
            })}

            {/* Upload New Button */}
            <button type="button" className={styles.uploadBtn} onClick={handleUploadClick}>
              <PlusOutlined className={styles.plusIcon} />
              <span>{uploadText}</span>
            </button>
          </div>
        </div>
      </Drawer>

      {/* Hidden File Input */}
      <input ref={fileInputRef} type="file" accept=".pdf,.docx,.txt" style={{ display: 'none' }} onChange={handleFileChange} />
    </div>
  );
};

export default MobileFileSelector;
